import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import ScrollToTop from "../../components/feature/ScrollToTop";
import SEOHead from "../../components/base/SEOHead";
import { liveScores, leagueList } from "../../mocks/liveScore";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://xn--9z2b23f95curk71m6qd.com";

const liveScoreJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "라이브스코어 스포츠중계",
  description: "실시간 경기 결과와 스코어를 가장 빠르게 확인하세요.",
  numberOfItems: liveScores.length,
  itemListElement: liveScores.map((score, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "SportsEvent",
      name: `${score.home} vs ${score.away}`,
      sport: score.league,
      homeTeam: { "@type": "SportsTeam", name: score.home },
      awayTeam: { "@type": "SportsTeam", name: score.away },
    },
  })),
};

export default function LiveScore() {
  const [activeLeague, setActiveLeague] = useState("전체");

  const filteredScores = useMemo(() => {
    if (activeLeague === "전체") return liveScores;
    return liveScores.filter((s) => s.league === activeLeague);
  }, [activeLeague]);

  const liveCount = liveScores.filter((s) => s.status === "live").length;

  return (
    <div className="min-h-screen bg-dark-bg">
      <SEOHead
        title="라이브스코어 스포츠중계 | 실시간 경기 결과 - 할매티비"
        description="실시간 라이브스코어 스포츠중계를 가장 빠르게 확인하세요. 해외축구, MLB, NBA 등 전 세계 스포츠 경기의 실시간 스코어와 결과를 1초 지연 없이 제공합니다."
        keywords="라이브스코어,실시간스코어,스포츠중계결과,축구스코어,MLB스코어,NBA스코어,경기결과"
        canonicalPath="/live-score"
        jsonLd={liveScoreJsonLd}
      />
      <Header />
      <main className="pt-24 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-white font-bold text-2xl md:text-3xl mb-2">
              라이브스코어 스포츠중계
            </h1>
            <p className="text-dark-text-muted text-base">
              실시간 경기 결과와 스코어를 가장 빠르게 확인하세요.
            </p>
          </div>

          {/* Live Counter */}
          <div className="bg-dark-surface border border-dark-border rounded-xl p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></span>
              <span className="text-white font-bold text-lg">실시간 생중계</span>
              <span className="text-brand-orange font-bold text-lg">{liveCount}경기</span>
            </div>
            <span className="text-dark-text-muted text-sm">실시간 업데이트 중</span>
          </div>

          {/* League Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8">
            {leagueList.map((league) => (
              <button
                key={league}
                onClick={() => setActiveLeague(league)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeLeague === league
                    ? "bg-brand-orange text-white"
                    : "bg-dark-surface text-dark-text-muted border border-dark-border hover:border-brand-orange hover:text-white"
                }`}
              >
                {league}
              </button>
            ))}
          </div>

          {/* Score Table */}
          <div className="space-y-3 mb-12">
            {filteredScores.map((score) => (
              <div
                key={score.id}
                className="bg-dark-surface border border-dark-border rounded-xl p-4 md:p-5 hover:border-brand-orange transition-all"
              >
                <div className="flex flex-col gap-3">
                  {/* Top: League + Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-dark-text-muted text-xs bg-dark-bg px-3 py-1 rounded-full">
                      {score.league}
                    </span>
                    <div className="flex items-center gap-2">
                      {score.isHot && (
                        <span className="text-red-500 text-xs font-bold bg-red-500/10 px-2 py-1 rounded-full">
                          HOT
                        </span>
                      )}
                      {score.status === "live" && (
                        <span className="flex items-center gap-1 text-red-500 text-xs font-bold bg-red-500/10 px-2 py-1 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                          {score.minute}
                        </span>
                      )}
                      {score.status === "finished" && (
                        <span className="text-dark-text-muted text-xs font-bold bg-dark-border px-2 py-1 rounded-full">
                          종료
                        </span>
                      )}
                      {score.status === "upcoming" && (
                        <span className="text-brand-orange text-xs font-bold bg-brand-orange/10 px-2 py-1 rounded-full">
                          {score.time}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Teams + Score */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1 text-right">
                      <span className="text-white font-bold text-base md:text-lg">{score.home}</span>
                    </div>
                    <div className="flex items-center gap-3 min-w-[120px] justify-center">
                      {score.status === "live" ? (
                        <>
                          <span className="text-white font-bold text-2xl">{score.homeScore}</span>
                          <span className="text-brand-orange font-bold">-</span>
                          <span className="text-white font-bold text-2xl">{score.awayScore}</span>
                        </>
                      ) : score.status === "finished" ? (
                        <>
                          <span className="text-dark-text-muted font-bold text-xl">{score.homeScore}</span>
                          <span className="text-dark-text-muted">-</span>
                          <span className="text-dark-text-muted font-bold text-xl">{score.awayScore}</span>
                        </>
                      ) : (
                        <span className="text-brand-orange font-bold text-xl">{score.time}</span>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <span className="text-white font-bold text-base md:text-lg">{score.away}</span>
                    </div>
                  </div>

                  {/* Odds */}
                  {score.homeOdds && (
                    <div className="flex items-center justify-center gap-4 pt-2 border-t border-dark-border">
                      <div className="text-center">
                        <span className="text-dark-text-muted text-xs block">홈</span>
                        <span className="text-brand-orange font-bold text-sm">{score.homeOdds}</span>
                      </div>
                      {score.drawOdds !== "-" && (
                        <div className="text-center">
                          <span className="text-dark-text-muted text-xs block">무</span>
                          <span className="text-dark-text-muted font-bold text-sm">{score.drawOdds}</span>
                        </div>
                      )}
                      <div className="text-center">
                        <span className="text-dark-text-muted text-xs block">원정</span>
                        <span className="text-brand-orange font-bold text-sm">{score.awayOdds}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* SEO Content */}
          <div className="border-t border-dark-border pt-8">
            <h2 className="text-white font-bold text-xl mb-4">라이브스코어 스포츠중계 이용 가이드</h2>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">라이브스코어 스포츠중계</strong>는 전 세계 스포츠 경기의 실시간 결과를 제공하는 핵심 서비스입니다. 축구, 야구, 농구, 배구 등 모든 종목의 실시간 스코어와 경기 상황을 1초 지연 없이 확인할 수 있습니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">라이브스코어 스포츠중계</strong>를 통해 경기의 흐름을 실시간으로 파악하고, 배당 변화와 팀 전략을 분석할 수 있습니다. 특히 <strong className="text-white">스포츠중계</strong>를 보면서 동시에 라이브스코어를 확인하면 더 깊은 경기 이해가 가능합니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed">
              <strong className="text-white">할매티비</strong>의 <strong className="text-white">라이브스코어 스포츠중계</strong>는 PC와 모바일에서 모두 최적화되어 있으며, 어디서나 빠르게 접속할 수 있습니다. 지금 바로 원하는 경기를 검색하고 실시간 스코어를 확인해 보세요.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center mt-12 pt-8 border-t border-dark-border">
            <Link
              to="/sports-schedule"
              className="group inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-base py-4 px-10 rounded-full transition-all whitespace-nowrap"
            >
              <i className="ri-calendar-schedule-line text-xl"></i>
              <span>전체 경기 일정 보기</span>
              <i className="ri-arrow-right-up-line text-lg opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"></i>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}